/**
 * Plantillas de proyecto ficticias (doc. 03 §8): «Taller base» (geometría de 6 elevadores del v6,
 * aprobada en la fase 0), su ejemplo sobrecargado y lienzo en blanco. Más los casos de verificación
 * del motor (doc. 05), empaquetados como proyectos para verlos en la aplicación.
 * Ningún dato de clientes reales: todo es ilustrativo.
 */

import { CATALOGO } from '../catalogo/catalogo';
import type {
  CitaDeAgenda,
  Elemento,
  Espacio,
  OrigenProyecto,
  Proyecto,
  Punto,
  TipoVia,
  Via,
} from '../nucleo/dominio/espacio';
import { VERSION_ESQUEMA } from '../nucleo/dominio/espacio';
import { ESPACIO_DE_TRABAJO_PROPIO } from '../nucleo/dominio/organizacion';
import { supuesto } from '../nucleo/dominio/parametros';
import type { DefinicionDeProceso } from '../nucleo/dominio/proceso';
import { configuracionAleatoriaPorDefecto, modeloPosventa, PERFIL_CITAS_V6 } from './modelo';
import { procesoP0, procesoP0ConRepuesto, procesoP1 } from './procesos';

export type GeneradorId = () => string;

export { ESPACIO_DE_TRABAJO_PROPIO } from '../nucleo/dominio/organizacion';

const OESTE = -Math.PI / 2;
const ESTE = Math.PI / 2;

function elemento(
  nuevoId: GeneradorId,
  catalogoId: string,
  x: number,
  z: number,
  rotacion = 0,
  extra: Partial<Elemento> = {},
): Elemento {
  const item = CATALOGO[catalogoId];
  if (!item) throw new Error(`Elemento de catálogo desconocido: ${catalogoId}`);
  return {
    id: nuevoId(),
    catalogoId,
    posicion: { x, z },
    rotacion,
    dimensiones: { ...item.dimensionesPorDefecto },
    ...extra,
  };
}

function via(nuevoId: GeneradorId, puntos: [number, number][], tipo: TipoVia): Via {
  return {
    id: nuevoId(),
    tipo,
    puntos: puntos.map(([x, z]): Punto => ({ x, z })),
    ancho: tipo === 'vehicular-doble' ? 6 : tipo === 'vehicular-un-sentido' ? 3.5 : 1.2,
  };
}

function proyectoBase(
  nuevoId: GeneradorId,
  nombre: string,
  origen: OrigenProyecto,
  ahora: string,
  espacios: Espacio[],
  descripcion = '',
): Proyecto {
  return {
    versionEsquema: VERSION_ESQUEMA,
    id: nuevoId(),
    espacioDeTrabajoId: ESPACIO_DE_TRABAJO_PROPIO,
    nombre,
    descripcion,
    origen,
    creadoEn: ahora,
    actualizadoEn: ahora,
    espacios,
  };
}

/** Espacio «Taller base»: 6 elevadores mixtos, 2 recepciones, 6 patio, 1 lavado, 3 entregas. */
export function espacioTallerBase(nuevoId: GeneradorId, nombre = 'Taller base · actual'): Espacio {
  const e = (catalogoId: string, x: number, z: number, r = 0, extra: Partial<Elemento> = {}) =>
    elemento(nuevoId, catalogoId, x, z, r, extra);
  const elementos: Elemento[] = [
    ...[12, 16, 20, 24, 28, 32].map((z) =>
      e('elevador', 4, z, OESTE, { propiedades: { tipoBahia: 'mixto' } }),
    ),
    ...[28, 31].map((z) => e('cajon-recepcion', 25, z, OESTE)),
    ...[42, 45, 48, 51, 54, 57].map((z) => e('cajon-patio', 24.5, z, ESTE)),
    e('lavado', 14, 72, 0),
    ...[22, 25, 28].map((x) => e('cajon-entrega', x, 71.5, 0)),
    e('oficina-asesores', 27, 16, 0, { etiqueta: 'Asesores de servicio' }),
    e('ingreso', 53, 20, 0),
    e('salida', 53, 60, 0),
  ];
  const vias: Via[] = [
    via(
      nuevoId,
      [
        [53, 2],
        [53, 78],
      ],
      'vehicular-doble',
    ),
    via(
      nuevoId,
      [
        [53, 30],
        [30, 30],
      ],
      'vehicular-doble',
    ),
    via(
      nuevoId,
      [
        [30, 27],
        [30, 34],
      ],
      'vehicular-doble',
    ),
    via(
      nuevoId,
      [
        [20, 8],
        [20, 66],
      ],
      'vehicular-doble',
    ),
    via(
      nuevoId,
      [
        [9, 8],
        [20, 8],
      ],
      'vehicular-doble',
    ),
    via(
      nuevoId,
      [
        [9, 8],
        [9, 66],
      ],
      'vehicular-un-sentido',
    ),
    via(
      nuevoId,
      [
        [9, 66],
        [36, 66],
      ],
      'vehicular-un-sentido',
    ),
    via(
      nuevoId,
      [
        [36, 66],
        [36, 34],
      ],
      'vehicular-un-sentido',
    ),
    via(
      nuevoId,
      [
        [36, 34],
        [53, 34],
      ],
      'vehicular-doble',
    ),
    via(
      nuevoId,
      [
        [20, 38],
        [36, 38],
      ],
      'vehicular-doble',
    ),
  ];
  return {
    id: nuevoId(),
    nombre,
    marco: { x: 30, z: 40, ancho: 60, fondo: 80 },
    layout: { elementos, vias, pisos: [], muros: [], versionLayout: 1 },
    modelo: modeloPosventa(),
    configuracionAleatoria: configuracionAleatoriaPorDefecto(),
  };
}

export function crearTallerBase(
  nuevoId: GeneradorId,
  ahora: string,
  nombre = 'Taller base',
): Proyecto {
  return proyectoBase(
    nuevoId,
    nombre,
    'plantilla:taller-base',
    ahora,
    [espacioTallerBase(nuevoId)],
    'Plantilla ficticia: 6 elevadores, 6 técnicos, 21 autos/día. Todos los valores son supuestos ilustrativos.',
  );
}

/** Demanda del ejemplo sobrecargado: la plantilla de la entrega 1.0 (38 autos, perfil del v6). */
export const DEMANDA_SOBRECARGADA = 38;

/**
 * El Taller base con la demanda de la entrega 1.0. Es el ejemplo de taller saturado y la carga de la
 * página de diagnóstico (la medición de rendimiento usa el caso más exigente).
 */
export function espacioTallerSobrecargado(
  nuevoId: GeneradorId,
  nombre = 'Taller base · sobrecargado',
): Espacio {
  const espacio = espacioTallerBase(nuevoId, nombre);
  espacio.modelo.demanda = {
    ...espacio.modelo.demanda,
    unidadesPorDia: supuesto(DEMANDA_SOBRECARGADA, 'autos/día'),
    perfilHorario: supuesto(PERFIL_CITAS_V6, 'fracción por hora'),
  };
  return espacio;
}

/**
 * Ejemplo aparte, no plantilla por defecto: sirve para ver un taller saturado (autos sin
 * recepcionar, patio lleno, promesas incumplidas).
 */
export function crearTallerSobrecargado(
  nuevoId: GeneradorId,
  ahora: string,
  nombre = 'Taller base · sobrecargado',
): Proyecto {
  return proyectoBase(
    nuevoId,
    nombre,
    'plantilla:taller-base',
    ahora,
    [espacioTallerSobrecargado(nuevoId)],
    'Ejemplo ficticio de taller saturado: el Taller base con 38 autos/día y el perfil de citas del v6.',
  );
}

/** Un espacio vacío de 60 × 80 m con la operación de posventa por defecto. */
export function espacioEnBlanco(nuevoId: GeneradorId, nombre = 'Espacio 1'): Espacio {
  return {
    id: nuevoId(),
    nombre,
    marco: { x: 30, z: 40, ancho: 60, fondo: 80 },
    layout: { elementos: [], vias: [], pisos: [], muros: [], versionLayout: 1 },
    modelo: modeloPosventa(),
    configuracionAleatoria: configuracionAleatoriaPorDefecto(),
  };
}

export function crearLienzo(
  nuevoId: GeneradorId,
  ahora: string,
  nombre = 'Lienzo en blanco',
): Proyecto {
  return proyectoBase(nuevoId, nombre, 'lienzo', ahora, [espacioEnBlanco(nuevoId)]);
}

// ───────────────────────── casos de verificación del motor ─────────────────────────

export interface CasoVerificacion {
  id: string;
  titulo: string;
  descripcion: string;
  /** Valores esperados calculados a mano (doc. 05 §3). */
  esperado: { indicador: string; nombre: string; valor: number; unidad: 'min' | '%' | 'autos' }[];
}

export const CASOS_VERIFICACION: readonly CasoVerificacion[] = [
  {
    id: 'M2',
    titulo: 'M2 · Una unidad',
    descripcion:
      'Un auto a las 08:00, un recurso de cada tipo, tiempos constantes y traslados de 2 min.',
    esperado: [
      { indicador: 'leadTimeListo', nombre: 'Lead time a listo', valor: 101, unidad: 'min' },
      { indicador: 'cumplimiento', nombre: 'Cumplimiento', valor: 100, unidad: '%' },
      {
        indicador: 'ocupacionBahias',
        nombre: 'Ocupación de elevadores',
        valor: 10.33,
        unidad: '%',
      },
      {
        indicador: 'productividadTecnica',
        nombre: 'Productividad técnica',
        valor: 10,
        unidad: '%',
      },
    ],
  },
  {
    id: 'M3',
    titulo: 'M3 · Dos unidades simultáneas',
    descripcion: 'Dos autos a las 08:00 compiten por un cajón, un asesor, una bahía y un técnico.',
    esperado: [
      { indicador: 'leadTimeListo', nombre: 'Lead time a listo', valor: 132, unidad: 'min' },
      { indicador: 'cumplimiento', nombre: 'Cumplimiento', valor: 50, unidad: '%' },
      {
        indicador: 'ocupacionBahias',
        nombre: 'Ocupación de elevadores',
        valor: 20.67,
        unidad: '%',
      },
    ],
  },
  {
    id: 'M4',
    titulo: 'M4 · El cuello no es la ocupación',
    descripcion: 'Cinco autos, 2 elevadores y 1 técnico: el técnico limita, no los elevadores.',
    esperado: [
      { indicador: 'leadTimeListo', nombre: 'Lead time a listo', valor: 225, unidad: 'min' },
      { indicador: 'cumplimiento', nombre: 'Cumplimiento', valor: 20, unidad: '%' },
      {
        indicador: 'ocupacionBahias',
        nombre: 'Ocupación de elevadores',
        valor: 25.83,
        unidad: '%',
      },
      { indicador: 'utilizacionTecnica', nombre: 'Utilización técnica', valor: 50, unidad: '%' },
    ],
  },
  {
    id: 'M5',
    titulo: 'M5 · Diagnóstico y aprobación',
    descripcion: 'Un auto de «otros servicios»: diagnóstico, aprobación del cliente y reparación.',
    esperado: [
      { indicador: 'leadTimeListo', nombre: 'Lead time a listo', valor: 206, unidad: 'min' },
      {
        indicador: 'productividadTecnica',
        nombre: 'Productividad técnica',
        valor: 20,
        unidad: '%',
      },
    ],
  },
  {
    id: 'M6',
    titulo: 'M6 · Espera de repuesto',
    descripcion:
      'A mitad del servicio falta un repuesto: 25 min con la bahía y el técnico retenidos.',
    esperado: [
      { indicador: 'leadTimeListo', nombre: 'Lead time a listo', valor: 126, unidad: 'min' },
      { indicador: 'ocupacionBahias', nombre: 'Ocupación de elevadores', valor: 14.5, unidad: '%' },
    ],
  },
  {
    id: 'M7',
    titulo: 'M7 · Cierre de jornada',
    descripcion: 'Un auto llega a las 17:30: el servicio termina en horas extra y queda pendiente.',
    esperado: [
      { indicador: 'autosListos', nombre: 'Autos listos', valor: 0, unidad: 'autos' },
      { indicador: 'pendientes', nombre: 'Trabajo pendiente al cierre', valor: 1, unidad: 'autos' },
    ],
  },
];

interface DefinicionCaso {
  proceso: () => DefinicionDeProceso;
  recepciones: number;
  bahias: number;
  asesores: number;
  tecnicos: number;
  citas: CitaDeAgenda[];
}

const cita = (hora: string, tipoUnidad = 'mantenimiento'): CitaDeAgenda => ({
  dia: 1,
  hora,
  tipoUnidad,
});

const DEFINICION_CASOS: Record<string, DefinicionCaso> = {
  M2: {
    proceso: procesoP0,
    recepciones: 1,
    bahias: 1,
    asesores: 1,
    tecnicos: 1,
    citas: [cita('08:00')],
  },
  M3: {
    proceso: procesoP0,
    recepciones: 1,
    bahias: 1,
    asesores: 1,
    tecnicos: 1,
    citas: [cita('08:00'), cita('08:00')],
  },
  M4: {
    proceso: procesoP0,
    recepciones: 5,
    bahias: 2,
    asesores: 1,
    tecnicos: 1,
    citas: Array.from({ length: 5 }, () => cita('08:00')),
  },
  M5: {
    proceso: procesoP1,
    recepciones: 1,
    bahias: 1,
    asesores: 1,
    tecnicos: 1,
    citas: [cita('08:00', 'otros')],
  },
  M6: {
    proceso: () => procesoP0ConRepuesto(),
    recepciones: 1,
    bahias: 1,
    asesores: 1,
    tecnicos: 1,
    citas: [cita('08:00')],
  },
  M7: {
    proceso: procesoP0,
    recepciones: 1,
    bahias: 1,
    asesores: 1,
    tecnicos: 1,
    citas: [cita('17:30')],
  },
};

/**
 * Proyecto con el caso de verificación: una vía recta con las estaciones detrás. Los traslados
 * usan 2 minutos fijos (parámetro de prueba del doc. 05 §2), no la distancia.
 */
export function crearCasoVerificacion(
  nuevoId: GeneradorId,
  ahora: string,
  casoId: string,
): Proyecto {
  const caso = CASOS_VERIFICACION.find((c) => c.id === casoId);
  const def = DEFINICION_CASOS[casoId];
  if (!caso || !def) throw new Error(`Caso de verificación desconocido: ${casoId}`);
  const elementos: Elemento[] = [];
  let x = 12;
  const fila = (catalogoId: string, n: number, extra: Partial<Elemento> = {}) => {
    for (let i = 0; i < n; i++) {
      elementos.push(elemento(nuevoId, catalogoId, x, 24.5, 0, extra));
      x += 4.5;
    }
    x += 2;
  };
  fila('cajon-recepcion', def.recepciones);
  fila('elevador', def.bahias, { propiedades: { tipoBahia: 'mixto' } });
  fila('lavado', 1);
  elementos.push(elemento(nuevoId, 'ingreso', 6, 20), elemento(nuevoId, 'salida', x + 2, 20));
  const largo = Math.max(60, x + 8);
  const modelo = modeloPosventa(def.proceso());
  modelo.dotacion = {
    asesor: supuesto(def.asesores, 'personas'),
    tecnico: supuesto(def.tecnicos, 'personas'),
  };
  modelo.demanda = {
    ...modelo.demanda,
    modo: 'agenda',
    unidadesPorDia: supuesto(def.citas.length, 'autos/día'),
    mezcla: supuesto({ mantenimiento: 1 }, 'fracción'),
    agenda: def.citas,
  };
  modelo.jornada.ultimaCita = supuesto('18:00', 'hh:mm');
  modelo.redondeoPromesaMin = supuesto(0, 'min');
  modelo.movimiento = { ...modelo.movimiento, modo: 'fijo', trasladoFijoMin: supuesto(2, 'min') };
  // Los casos del doc. 05 se calcularon con el lavado que sigue el turno (M7).
  modelo.politicas = {
    seleccionEstacion: 'menor-codigo',
    seleccionPersona: 'menor-codigo',
    lavadoDespuesDeHora: false,
  };
  const espacio: Espacio = {
    id: nuevoId(),
    nombre: caso.titulo,
    marco: { x: largo / 2, z: 25, ancho: largo, fondo: 20 },
    layout: {
      elementos,
      vias: [
        via(
          nuevoId,
          [
            [2, 20],
            [largo - 2, 20],
          ],
          'vehicular-doble',
        ),
      ],
      pisos: [],
      muros: [],
      versionLayout: 1,
    },
    modelo,
    configuracionAleatoria: { semilla: 1, replicas: 5, replicasAutomaticas: false, dias: 1 },
  };
  return proyectoBase(
    nuevoId,
    `Caso ${caso.titulo}`,
    `caso:${caso.id}`,
    ahora,
    [espacio],
    caso.descripcion,
  );
}
